<template>
  <article
    class="permission-page-card"
    :class="{ 'permission-page-card--open': expanded }"
    :data-page-id="page.id"
  >
    <div v-if="focused" class="focused-page-title">
      <v-icon small>{{ page.icon }}</v-icon>
      <h3>{{ page.label }}</h3>
      <span
        class="access-badge"
        :class="{ 'access-badge--enabled': state.view }"
        >{{ accessSummary }}</span
      >
    </div>
    <button
      v-else
      type="button"
      class="page-toggle"
      :aria-expanded="expanded"
      :aria-controls="'catalog-page-' + page.id"
      @click="$emit('toggle')"
    >
      <v-icon small>{{ page.icon }}</v-icon
      ><span class="page-copy"
        ><strong>{{ page.label }}</strong
        ><small v-if="page.description">{{ page.description }}</small></span
      >
      <span
        class="access-badge"
        :class="{ 'access-badge--enabled': state.view }"
        >{{ accessSummary }}</span
      ><v-icon small>{{
        expanded ? "mdi-chevron-up" : "mdi-chevron-down"
      }}</v-icon>
    </button>
    <div v-if="expanded" :id="'catalog-page-' + page.id" class="page-detail">
      <div class="view-control">
        <div>
          <strong>View this page</strong>
          <p>People with this role can open {{ page.label }}.</p>
        </div>
        <v-switch
          :input-value="state.view"
          :aria-label="'View page — ' + page.label"
          :disabled="!editable"
          hide-details
          inset
          class="action-switch"
          @change="$emit('view-change', $event)"
        />
      </div>
      <section
        v-for="group in visibleGroups"
        :key="group.id"
        class="task-group"
      >
        <h4>{{ group.label }}</h4>
        <p
          v-if="group.description && showExplanations"
          class="group-description"
        >
          {{ group.description }}
        </p>
        <div
          v-for="action in group.actions"
          :key="action.id"
          class="task-action"
          :class="{ 'task-action--locked': !!restriction(action) }"
        >
          <div class="action-copy">
            <div class="action-label">
              <strong>{{ action.label }}</strong>
            </div>
            <p v-if="showExplanations">{{ action.description }}</p>
            <p v-if="restriction(action)" class="restriction-copy">
              <v-icon x-small>mdi-lock-outline</v-icon>
              {{ restriction(action) }}
            </p>
            <p
              v-else-if="action.requiredRoles && showExplanations"
              class="rule-copy"
            >
              Current workflow checks role and record status.
            </p>
          </div>
          <v-switch
            :input-value="state.actions[action.id]"
            :aria-label="action.label + ' — ' + page.label"
            :disabled="!editable || !!restriction(action)"
            hide-details
            inset
            class="action-switch"
            @change="
              $emit('action-change', { actionId: action.id, value: $event })
            "
          />
        </div>
      </section>
      <p v-if="!page.taskGroups.length" class="simple-page-note">
        Viewing this page is its only permission.
      </p>
      <section v-if="page.recordScope" class="scope-area">
        <h4>{{ page.recordScope.label }}</h4>
        <v-radio-group
          :value="state.scope"
          :disabled="!editable || !state.view"
          hide-details
          @change="$emit('scope-change', $event)"
          ><v-radio
            v-for="option in page.recordScope.options"
            :key="option.id"
            :label="option.label"
            :value="option.id"
        /></v-radio-group>
        <p>{{ page.recordScope.description }}</p>
      </section>
    </div>
  </article>
</template>
<script>
import { pageActions, roleRestriction } from "./permission-catalog";
export default {
  props: {
    page: { type: Object, required: true },
    state: { type: Object, required: true },
    expanded: Boolean,
    focused: Boolean,
    showExplanations: { type: Boolean, default: true },
    editable: Boolean,
    roleName: String,
    search: { type: String, default: "" },
  },
  computed: {
    accessSummary() {
      if (!this.state.view) return "No access";
      const count = pageActions(this.page).filter(
        (action) => this.state.actions[action.id],
      ).length;
      return count ? `${count + 1} actions allowed` : "View only";
    },
    visibleGroups() {
      const term = this.search.trim().toLowerCase();
      if (!term || this.page.label.toLowerCase().includes(term))
        return this.page.taskGroups;
      return this.page.taskGroups
        .map((group) => ({
          ...group,
          actions: group.actions.filter((action) =>
            `${group.label} ${group.description || ""} ${action.label} ${action.description}`
              .toLowerCase()
              .includes(term),
          ),
        }))
        .filter((group) => group.actions.length);
    },
  },
  methods: {
    restriction(action) {
      return roleRestriction(action, this.roleName);
    },
  },
};
</script>
<style scoped>
.permission-page-card {
  container: permission-page / inline-size;
  border: 1px solid #e1e8f0;
  border-radius: 9px;
  margin-bottom: 8px;
  overflow: hidden;
  background: #fff;
}
.focused-page-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px 16px;
  background: #f5f8fc;
}
.focused-page-title h3 {
  flex: 1;
  font-size: 15px;
  font-weight: 700;
  color: #304b67;
  margin: 0;
}
.focused-page-title .v-icon {
  color: #6e87a1;
}
.permission-page-card--open {
  border-color: #b6d2f1;
}
.page-toggle {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
  text-align: left;
  padding: 14px 12px;
}
.page-toggle:hover {
  background: #f7faff;
}
.permission-page-card--open .page-toggle {
  background: #f1f7ff;
}
.page-toggle:focus-visible {
  outline: 2px solid #1976d2;
  outline-offset: -2px;
}
.page-toggle > .v-icon {
  color: #72869d;
}
.page-copy {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.page-copy strong {
  font-size: 13px;
  color: #30465e;
}
.page-copy small {
  font-size: 11px;
  color: #74869a;
  line-height: 1.5;
}
.access-badge {
  font-size: 10px;
  white-space: nowrap;
  padding: 3px 7px;
  background: #f0f3f7;
  color: #708195;
  border-radius: 5px;
}
.access-badge--enabled {
  background: #e9f5ee;
  color: #337652;
}
.page-detail {
  padding: 0 15px 15px;
  border-top: 1px solid #dfebf9;
}
p {
  margin: 4px 0 0;
  line-height: 1.6;
  color: #738397;
  font-size: 11px;
}
.view-control {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 17px 0;
}
.view-control strong {
  font-size: 13px;
  color: #233d59;
}
.action-switch {
  margin: 0;
  padding: 0;
  flex: 0 0 50px;
  min-width: 50px;
}
.task-group {
  border-top: 1px solid #e9eef5;
  padding-top: 15px;
  margin-top: 2px;
}
h4 {
  color: #708298;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin: 0 0 8px;
}
.group-description {
  margin: -3px 0 12px;
}
.task-action {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  padding: 9px 0;
}
.action-copy {
  min-width: 0;
  flex: 1;
}
.action-label {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
}
.action-label strong {
  font-size: 12px;
  color: #344b64;
}
.preview-tag {
  color: #8b6c3e;
  background: #faf3e8;
  border: 1px solid #efe3cd;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 9px;
}
.task-action--locked .action-label strong {
  color: #7e8c9d;
}
.restriction-copy {
  color: #9a7539;
}
.restriction-copy .v-icon {
  color: inherit;
}
.rule-copy {
  color: #698677;
}
.scope-area {
  padding: 18px 0 0;
  margin-top: 14px;
  border-top: 1px solid #e9eef5;
}
.scope-area .v-input {
  margin-top: 12px;
}
.simple-page-note {
  padding: 0 0 5px;
}
@container permission-page (min-width: 600px) {
  .task-group {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 28px;
  }
  .task-group > h4,
  .task-group > .group-description {
    grid-column: 1 / -1;
  }
  .task-action {
    min-width: 0;
  }
}
@media (max-width: 550px) {
  .page-toggle {
    gap: 7px;
    padding: 13px 10px;
  }
  .page-copy small {
    display: none;
  }
  .access-badge {
    font-size: 9px;
  }
  .page-detail {
    padding: 0 12px 12px;
  }
}
</style>
